import React, { useContext, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  EmailOutlined,
  ForumOutlined,
  LockOutlined,
  PeopleOutlineOutlined,
  ShowChartOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const FEATURES = [
  { icon: <ForumOutlined />, text: "Unified WhatsApp inbox for your team" },
  {
    icon: <PeopleOutlineOutlined />,
    text: "Multi-agent real-time collaboration",
  },
  { icon: <ShowChartOutlined />, text: "Ticket analytics and reporting" },
];

const STATS = [
  { value: "10k+", label: "Teams" },
  { value: "99.9%", label: "Uptime" },
  { value: "< 1s", label: "Delivery" },
];

const Login = () => {
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) =>
    setUser((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* ── Brand panel (desktop only) ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          width: "46%",
          flexShrink: 0,
          bgcolor: "primary.main",
          color: "#fff",
          p: { md: 5, lg: 7 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background circles */}
        <Box
          sx={{
            position: "absolute",
            width: 480,
            height: 480,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.05)",
            top: -140,
            right: -140,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 320,
            height: 320,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.04)",
            bottom: -80,
            left: -80,
            pointerEvents: "none",
          }}
        />

        {/* Top: logo + headline */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 52,
              height: 52,
              borderRadius: 2.5,
              bgcolor: "rgba(255,255,255,0.15)",
              mb: 2.5,
            }}
          >
            <ForumOutlined sx={{ fontSize: 28 }} />
          </Box>
          <Typography
            variant="h3"
            fontWeight={800}
            letterSpacing="-0.5px"
            lineHeight={1.2}
          >
            Hashlix CRM
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 1.5, opacity: 0.75, maxWidth: 300, lineHeight: 1.65 }}
          >
            The WhatsApp-native CRM for modern support teams.
          </Typography>
        </Box>

        {/* Middle: feature list */}
        <Stack spacing={1.5} sx={{ position: "relative", zIndex: 1 }}>
          {FEATURES.map((f) => (
            <Box
              key={f.text}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                bgcolor: "rgba(255,255,255,0.10)",
                borderRadius: 2.5,
                px: 2.5,
                py: 2,
              }}
            >
              <Box sx={{ opacity: 0.85, display: "flex", flexShrink: 0 }}>
                {f.icon}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, opacity: 0.92, lineHeight: 1.4 }}
              >
                {f.text}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Bottom: stat chips */}
        <Box
          sx={{ display: "flex", gap: 1.5, position: "relative", zIndex: 1 }}
        >
          {STATS.map((s) => (
            <Box
              key={s.label}
              sx={{
                flex: 1,
                bgcolor: "rgba(255,255,255,0.10)",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                {s.value}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.65 }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Form panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "background.default",
          p: { xs: 3, sm: 5, md: 6 },
        }}
      >
        {/* Mobile brand mark */}
        <Box sx={{ display: { md: "none" }, textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 52,
              height: 52,
              borderRadius: 2.5,
              bgcolor: "primary.main",
              color: "#fff",
              mb: 1.5,
            }}
          >
            <ForumOutlined sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Hashlix CRM
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack spacing={3}>
            {/* Heading */}
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Welcome back
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to your workspace to continue
              </Typography>
            </Box>

            <Divider />

            {/* Fields */}
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  label={i18n.t("login.form.email")}
                  name="email"
                  type="email"
                  fullWidth
                  required
                  value={user.email}
                  onChange={handleChangeInput}
                  autoComplete="email"
                  autoFocus
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined
                            fontSize="small"
                            sx={{ color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <TextField
                  label={i18n.t("login.form.password")}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  required
                  value={user.password}
                  onChange={handleChangeInput}
                  autoComplete="current-password"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            fontSize="small"
                            sx={{ color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={() => setShowPassword((p) => !p)}
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.4,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  {i18n.t("login.buttons.submit")}
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Typography variant="body2" align="center" color="text.secondary">
              {i18n.t("login.buttons.register")}{" "}
              <Link
                component={RouterLink}
                to="/signup"
                color="primary"
                fontWeight={600}
                underline="hover"
              >
                Sign up free
              </Link>
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
